/**
 * Change url parameters and returns new url
 * @param {'set'|'delete'} type what do we want to do with the url parameters
 * @param {Array<{key: string, value: string}>|Array<string>} data for type: `set` data should be an array of objects with `key` and `value`;
 * for type: `delete` it should an array of strings
 *
 * @returns {string} updated url string
 */
export const getUpdatedUrl = (type, data) => {
    const urlParams = new URLSearchParams(window.location.search);
    switch (type) {
        case 'set':
            data.forEach((item) => {
                urlParams.set(item.key, item.value);
            });
            break;
        case 'delete':
            data.forEach((key) => {
                urlParams.delete(key);
            });
            break;
        default:
            break;
    }
    return `?${urlParams.toString()}${window.location.hash}`;
};

/**
 * Update browser session history stack
 * @param {string} url new url to update
 * @param {boolean} addHistoryEntry create new history entry (user can go back and forward in history). Default `false`
 *
 * @returns {void}
 */
export const updateHistory = (url, addHistoryEntry) => {
    if (addHistoryEntry) {
        window.history.pushState(url, '', url);
    } else {
        window.history.replaceState(url, '', url);
    }
};

/**
 * Get paramater from the url
 * @param {string} key url parameter
 *
 * @returns {string|null|undefined} parameter's value or `null` if such parameter doesn't exist or `undefined` if `key` isn't provided
 */
export const getParameter = (key) => {
    if (key) {
        return new URLSearchParams(window.location.search).get(key);
    }
    return false;
};

/**
 * Get all parameters with values from the url
 * @returns {Array<{key: string, value: string}>|Array<never>} array of objects with `key` and `value` properties or empty array
 */
export const getParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = [];
    /* eslint-disable */
    for (const [key, value] of urlParams.entries()) {
        params.push({ key, value });
    }
    return params;
    /* eslint-enable */
};

/**
 * Set parameters of the url
 * @param {Array<{key: string, value: string}>} parameters an array of objects with `key` and `value` properties
 * @param {boolean} addHistoryEntry create new history entry (user can go back and forward in history). Default `false`
 *
 * @returns {void}
 */
export const setParameters = (parameters, addHistoryEntry = false) => {
    if (!Array.isArray(parameters)) {
        return;
    }
    const url = this.getUpdatedUrl('set', parameters);
    this.updateHistory(url, addHistoryEntry);
};

/**
 * Set parameter of the url
 * @param {string} key url parameter
 * @param {string} value value of the url parameter
 * @param {boolean} addHistoryEntry create new history entry (user can go back and forward in history). Default `false`
 *
 * @returns {void}
 */
export const setParameter = (key, value, addHistoryEntry = false) => {
    if (!key || value === undefined) {
        return;
    }
    this.setParameters([{ key, value }], addHistoryEntry);
};

/**
 * Delete parameters from the url
 * @param {Array<string>} keys an array of strings
 * @param {boolean} addHistoryEntry create new history entry (user can go back and forward in history). Default `false`
 *
 * @returns {void}
 */
export const deleteParameters = (keys, addHistoryEntry = false) => {
    if (!Array.isArray(keys)) {
        return;
    }
    const url = this.getUpdatedUrl('delete', keys);
    this.updateHistory(url, addHistoryEntry);
};

/**
 * Delete parameter from the url
 * @param {string} key url parameter
 * @param {boolean} addHistoryEntry create new history entry (user can go back and forward in history). Default `false`
 *
 * @returns {void}
 */
export const deleteParameter = (key, addHistoryEntry = false) => {
    if (!key) {
        return;
    }
    this.deleteParameters([key], addHistoryEntry);
};

/**
 * Clear url
 * @param {boolean} addHistoryEntry create new history entry (user can go back and forward in history). Default `false`
 *
 * @returns {void}
 */
export const clearUrl = (addHistoryEntry = false) => {
    this.updateHistory(window.location.pathname, addHistoryEntry);
};
